import { Alert, Button, Popconfirm } from 'antd'
import { useCurrentUser, useUpdateUser } from 'api'
import Container from 'components/Container'
import FormikInput from 'components/FormikInput'
import { useHideModal } from 'components/Modals'
import { Formik } from 'formik'
import { Form, FormItem } from 'formik-antd'
import { useLocalization } from 'localization'
import { useMemo } from 'react'
import { createValidationSchema } from 'utils/validation'
import Skeleton from './skeleton'
import { useIsWealthicaUser } from 'modules/common/hooks'

const schema = createValidationSchema(yup =>
	yup.object({
		firstName: yup.string().required(),
		lastName: yup.string().required(),
		email: yup.string().email().required(),
	})
)

interface IFormValues {
	firstName: string
	lastName: string
	email: string
}

const PersonalTab = () => {
	const { t } = useLocalization()
	const { data: user, isLoading, isFetched } = useCurrentUser()
	const { mutateAsync: updateUser, isLoading: isUpdateLoading } = useUpdateUser()
	const hideModal = useHideModal()
	const isWealthicaUser = useIsWealthicaUser()

	const isUpgradeAllowed = useMemo(() => !user?.isCPA && !isWealthicaUser, [user, isWealthicaUser])

	const onSubmit = async (values: IFormValues) => {
		await updateUser({
			id: user!.id,
			firstName: values.firstName,
			lastName: values.lastName,
		})
		hideModal()
	}

	const initialValue: IFormValues = {
		firstName: user?.firstName || '',
		lastName: user?.lastName || '',
		email: user?.email || '',
	}

	return (
		<Formik initialValues={initialValue} enableReinitialize validationSchema={schema} onSubmit={onSubmit}>
			{({ dirty, isValid, isSubmitting }) => (
				<Form layout="vertical">
					{isLoading && !isFetched ? (
						<Skeleton />
					) : (
						<>
							{isUpgradeAllowed && (
								<Container display="flex" alignItems="center" justifyContent="space-between" margin={{ bottom: 'md' }}>
									<Alert
										message={t('settings.personal.accountantUpgradeText')}
										action={
											<Popconfirm
												title={t('settings.personal.upgradeConfirmation')}
												onConfirm={async () => {
													await updateUser({ id: user!.id, isCpa: true })
													hideModal()
												}}
												okText={t('actions.confirm')}
												cancelText={t('actions.cancel')}
												icon={null}
											>
												<Button size="small" type="link" disabled={isUpdateLoading}>
													{t('actions.upgrade')}
												</Button>
											</Popconfirm>
										}
									/>
								</Container>
							)}
							<FormItem name="firstName" label={t('settings.personal.firstName')}>
								<FormikInput name="firstName" placeholder={t('placeholders.firstName')} />
							</FormItem>
							<FormItem name="lastName" label={t('settings.personal.lastName')}>
								<FormikInput name="lastName" placeholder={t('placeholders.lastName')} />
							</FormItem>
							<FormItem name="email" label={t('terms.email')}>
								<FormikInput name="email" placeholder={t('placeholders.email')} disabled />
							</FormItem>
						</>
					)}
					<Button type="primary" htmlType="submit" loading={isSubmitting} disabled={!isValid || !dirty}>
						{t('actions.save')}
					</Button>
				</Form>
			)}
		</Formik>
	)
}

export default PersonalTab
